/**
 * The door to today's version of a public page whose rebuild is live (2026-10-01).
 *
 * App.tsx declares each such page as `withPavilion(todaysPage('Pricing'), 'pricing')`: the new design's door shows the
 * rebuild, and this door is its fallback, rendered only when the rebuild's chunks cannot be loaded or the page's word
 * leaves `liveOnPrest0ai`. Today's pages are their own chunk, `todays-pages` (src/pages/todaysPublicPages.ts), and
 * this is its one door: before, they were static imports of App.tsx, on the first download of every page.
 *
 * WHY useDeferredModule AND NOT React.lazy: App.tsx records React.lazy stalling on the FIRST mount of a public page in
 * production (2026-05-09); the hook is a plain import() resolved into state, with no Suspense and nothing thrown while
 * it loads. The same hook serves today's home page (LandingPageDoor.tsx) and every page of the new design. It retries a
 * failed download three times before handing a chunk that is gone for good to the stale-tab self-heal.
 */
import React from 'react';
import { useDeferredModule } from '../utils/useDeferredModule';
import { semanticLight } from '../styles/semanticTokens.generated';
import type * as TodaysPages from '../pages/todaysPublicPages';

// The ONE import of the chunk (R4 counts it).
const loadTodaysPages = () => import(/* webpackChunkName: "todays-pages" */ '../pages/todaysPublicPages');

export type TodaysPageName = keyof typeof TodaysPages;

/** Today's version of one page, loaded on demand. One component per page, made once where App.tsx declares it. */
export function todaysPage(name: TodaysPageName): React.FC {
  const TodaysPage: React.FC = () => {
    const { module, error } = useDeferredModule(loadTodaysPages, 'todays-pages', { start: 'mount' });
    if (module) {
      const Page = module[name] as React.ComponentType;
      return <Page />;
    }
    // Never a blank page on a chunk that failed: the download has been retried and a chunk that is gone for good
    // handed to the stale-tab self-heal; what reaches here is the error boundary's, as for every deferred page.
    if (error) throw error;
    // While the chunk arrives: today's pages' own ground, so the page fills in calmly.
    return <div data-todays-page-door={name} aria-busy="true" style={{ minHeight: '100vh', background: semanticLight.canvas }} />;
  };
  TodaysPage.displayName = `todaysPage(${name})`;
  return TodaysPage;
}
